import { KnowledgeArticle, createArticle } from '../types'

export const tprmArticles: KnowledgeArticle[] = [
  createArticle({
    id: 'tprm-overview',
    title: 'Third-Party Risk Management Overview',
    section: 'tprm',
    summary: 'TPRM manages vendor risk through assessments, questionnaires, and continuous monitoring. Track vendor criticality, compliance status, and security posture.',
    content: `
# Third-Party Risk Management (TPRM)

TPRM helps you assess and manage the security risks posed by your vendors and third-party service providers.

## Why TPRM Matters

- Vendors can be a source of data breaches
- Compliance frameworks require vendor oversight
- Supply chain attacks are increasing
- Due diligence protects your organization

## Key Concepts

### Vendor Tiers
Vendors are classified by criticality:
- **Critical** - Essential to operations, access to sensitive data
- **High** - Important vendors with significant access
- **Medium** - Standard vendors with limited access
- **Low** - Minimal risk vendors

### Risk Domains
Assessments cover multiple domains:
- Security practices
- Data handling
- Business continuity
- Compliance certifications
- Financial stability

## Features

- **Vendor Profiles** - Central repository of vendor information
- **Risk Assessments** - Structured evaluation process
- **Questionnaires** - Send and track security questionnaires
- **Dark Web Monitoring** - Check for vendor credential exposure
- **Continuous Monitoring** - Ongoing risk tracking
- **Reports** - Executive and detailed risk reports
    `.trim(),
    tags: ['tprm', 'vendors', 'third-party', 'risk', 'assessment', 'supply-chain'],
    relatedPages: ['/tprm'],
    icon: 'Building2',
    order: 1,
  }),

  createArticle({
    id: 'vendor-assessments',
    title: 'Conducting Risk Assessments',
    section: 'tprm',
    summary: 'Assess vendors using built-in questionnaires covering security, compliance, and operations. Score responses and generate risk ratings automatically.',
    content: `
# Conducting Vendor Risk Assessments

Learn how to assess vendor risk using the platform's assessment tools.

## Assessment Process

### 1. Add Vendor
1. Go to **TPRM** → **Vendors**
2. Click **Add Vendor**
3. Enter vendor details:
   - Name and description
   - Industry/category
   - Criticality tier
   - Contact information
   - Services provided

### 2. Create Assessment
1. Click **New Assessment** on the vendor
2. Select assessment type:
   - Security Assessment
   - SOC 2 Review
   - Custom Assessment
3. Set due date and assignee

### 3. Send Questionnaire
1. Choose a questionnaire template or create custom
2. Enter vendor contact email
3. Vendor receives link to complete questionnaire
4. Track completion status

### 4. Review Responses
1. Review each questionnaire response
2. Score answers (pass/fail/partial)
3. Request clarification if needed
4. Mark assessment complete

### 5. Generate Risk Score
The platform calculates:
- Individual domain scores
- Overall risk rating
- Comparison to previous assessments

## Assessment Templates

Built-in templates include:
- SIG Lite (Standard Information Gathering)
- SOC 2 Readiness
- HIPAA Vendor Assessment
- PCI DSS Service Provider
- Custom questionnaires
    `.trim(),
    tags: ['tprm', 'assessment', 'questionnaire', 'risk-score', 'due-diligence'],
    relatedPages: ['/tprm'],
    icon: 'ClipboardCheck',
    order: 2,
  }),

  createArticle({
    id: 'dark-web-vendors',
    title: 'Vendor Dark Web Monitoring',
    section: 'tprm',
    summary: 'Monitor vendor domains for credential exposure on the dark web. Get alerts when vendor employees appear in data breaches or stealer logs.',
    content: `
# Vendor Dark Web Monitoring

Monitor your vendors for credential exposure and data breaches on the dark web.

## What's Monitored

- **Breached Emails** - Vendor emails found in data breaches
- **Stealer Logs** - Credentials stolen by info-stealer malware
- **Leaked URLs** - Vendor URLs found in credential dumps
- **Third-Party Exposure** - Partners of your vendors

## Data Sources

- **Have I Been Pwned (HIBP)** - Comprehensive breach database
- **Hudson Rock** - Info-stealer intelligence
- **Additional sources** - Paste sites, dark web forums

## Viewing Dark Web Alerts

1. Go to **TPRM** → select a vendor
2. Click the **Dark Web** tab
3. View alerts by severity:
   - Critical - Many credentials exposed
   - High - Significant exposure
   - Medium - Some exposure
   - Low - Minimal exposure

## Understanding Results

### Breached Emails
Emails found in known data breaches. Consider:
- How recent is the breach?
- What data was exposed?
- Has the vendor addressed it?

### Stealer Logs
Credentials captured by malware. Higher risk because:
- May include current passwords
- Indicates infected systems
- Could affect multiple accounts

## Taking Action

1. Alert the vendor about exposure
2. Request remediation plan
3. Adjust vendor risk score
4. Document in assessment
5. Consider additional controls
    `.trim(),
    tags: ['tprm', 'dark-web', 'breaches', 'credentials', 'monitoring', 'vendors'],
    relatedPages: ['/tprm', '/dark-web'],
    icon: 'AlertTriangle',
    order: 3,
  }),
]
